import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSessionUser } from '@/shared/session/currentUser';

import { NOTICES_PAGE, noticeKeys, noticesApi } from './api';
import type { NoticeWrite, PendingNotices } from './types';

/**
 * How often the window and the bell look for a new notice: a tariff published at ten
 * reaches whoever has the platform open before the next coffee, without anybody reloading.
 * The same minute as the inbox.
 */
export const NOTICES_POLL_MS = 60_000;

/**
 * How long this tab remembers what it marked seen: well past the answer of «Entendido» and
 * the poll after it. Nothing observes that list, so without its own time the cache would
 * drop it as soon as it is written wherever queries are kept for less.
 */
const SEEN_HERE_MS = 10 * 60_000;

/** `pending` without what this tab already marked seen. */
function withoutSeenHere(data: PendingNotices, seen: readonly string[] | undefined) {
  if (!seen || seen.length === 0) return data;
  const drop = new Set(seen);
  const items = data.items.filter((notice) => !drop.has(notice.id));
  const removed = data.items.length - items.length;
  if (removed === 0) return data;
  return { ...data, items, unread: Math.max(0, data.unread - removed) };
}

function seenHere(client: QueryClient): string[] | undefined {
  return client.getQueryData<string[]>(noticeKeys.seenHere);
}

/**
 * Whether the notices may be asked for: there is a session, and the person need not change
 * the password they were given first (the API refuses everything else until then).
 */
export function useNoticesAllowed(): boolean {
  const user = useSessionUser();
  return Boolean(user) && !user?.must_change_password;
}

/**
 * What the window shows on coming in, and the number on the bell. Only while
 * `useNoticesAllowed`: whoever calls `refetch` checks it too, since `refetch` asks anyway.
 */
export function usePendingNotices() {
  const client = useQueryClient();
  const allowed = useNoticesAllowed();
  return useQuery({
    queryKey: noticeKeys.pending,
    queryFn: noticesApi.pending,
    enabled: allowed,
    refetchInterval: NOTICES_POLL_MS,
    staleTime: 30_000,
    select: (data) => withoutSeenHere(data, seenHere(client)),
  });
}

/** The bell's list, asked only while it is open. */
export function useNoticeList(open: boolean, limit: number = NOTICES_PAGE) {
  return useQuery({
    queryKey: noticeKeys.list(limit),
    queryFn: () => noticesApi.list(limit),
    enabled: open,
    placeholderData: (previous) => previous,
  });
}

/**
 * «Entendido»: the window and the bell forget them at once, and ask again after. What was
 * marked here stays out of `pending` even if an answer that left before the mark arrives
 * after it: following a notice's link from the bell changes the screen, and the window asks
 * at that same moment. Kept in the cache for `SEEN_HERE_MS` (by then the back has it), and
 * forgotten on logging out with everything else.
 */
export function useMarkNoticesSeen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => noticesApi.seen(ids),
    onMutate: (ids) => {
      const seen = [...new Set([...(seenHere(client) ?? []), ...ids])];
      client.setQueryDefaults(noticeKeys.seenHere, { gcTime: SEEN_HERE_MS });
      client.setQueryData(noticeKeys.seenHere, seen);
      client.setQueryData<PendingNotices>(noticeKeys.pending, (old) =>
        old ? withoutSeenHere(old, seen) : old,
      );
    },
    onSettled: () => client.invalidateQueries({ queryKey: noticeKeys.all }),
  });
}

export function useCreateNotice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: NoticeWrite) => noticesApi.create(payload),
    onSuccess: () => client.invalidateQueries({ queryKey: noticeKeys.all }),
  });
}

export function useWithdrawNotice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => noticesApi.withdraw(id),
    onSuccess: () => client.invalidateQueries({ queryKey: noticeKeys.all }),
  });
}

/**
 * Whom the window to write a notice offers (contract v10.55). A back before v10.55 answers
 * 404 here: the window then writes for the whole team, as it did.
 */
export function useNoticeAudience() {
  return useQuery({
    queryKey: noticeKeys.audience,
    queryFn: noticesApi.audience,
    staleTime: 0,
    retry: false,
  });
}

/** Whether the publish window can offer the email to «Comerciales». */
export function useSalespeopleStatus(enabled: boolean) {
  return useQuery({
    queryKey: noticeKeys.salespeople,
    queryFn: noticesApi.salespeople,
    enabled,
    staleTime: 0,
  });
}
